import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import profileImg from "../assets/img/profile-banner.png";
import { pageScrollY } from "../hooks/useHomeMotion";

const linkedin = "https://www.linkedin.com/in/sukrit-phaopanich/";

export const NavBar = ({ theme, onToggleTheme, onOpenContact, musicOn, onToggleMusic, transitionTo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const goTo = transitionTo || navigate;
  const onAbout = location.pathname === "/about";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [onLight, setOnLight] = useState(onAbout);

  useEffect(() => {
    const onScroll = () => {
      const y = pageScrollY();
      setScrolled(y > 48);
      const hero = document.querySelector(".hero");
      const heroH = hero ? hero.offsetHeight * 0.72 : 0;
      const impact = document.querySelector(".testimonials");
      const about = document.querySelector(".about-intro");
      const footer = document.querySelector(".site-footer, footer, #contact");
      const impactTop = impact?.getBoundingClientRect().top ?? Infinity;
      const aboutTop = about?.getBoundingClientRect().top ?? Infinity;
      const footerTop = footer?.getBoundingClientRect().top ?? Infinity;
      const overDark =
        (impact && impactTop <= 88 && (!about || aboutTop > 88)) ||
        (footer && footerTop <= 88);
      setOnLight(hero ? y > heroH && !overDark : !overDark);
    };
    onScroll();
    const opts = { passive: true };
    const targets = [window, document.documentElement, document.body, window.visualViewport].filter(Boolean);
    targets.forEach((target) => target.addEventListener("scroll", onScroll, opts));
    window.visualViewport?.addEventListener("resize", onScroll, opts);
    return () => {
      targets.forEach((target) => target.removeEventListener("scroll", onScroll, opts));
      window.visualViewport?.removeEventListener("resize", onScroll, opts);
    };
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeCase = () => {
    if (!document.documentElement.classList.contains("case-open")) return;
    window.history.pushState(null, "", "#work");
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  const go = (id) => {
    setOpen(false);
    if (id === "about") {
      goTo("/about");
      return;
    }
    if (id === "home" || id === "work") {
      const hash = id === "work" ? "work" : "home";
      if (location.pathname !== "/") {
        goTo({ pathname: "/", hash });
        return;
      }
      closeCase();
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    closeCase();
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      className={`site-nav ${scrolled ? "scrolled" : ""} ${open ? "menu-open" : ""} ${
        onLight ? "nav-on-light" : "nav-on-dark"
      }`}
      id="nav"
    >
      <div className="nav-side">
        <span className="nav-pin" aria-hidden="true">
          <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
            <path
              d="M7 17s6-6.1 6-10.2A6 6 0 1 0 1 6.8C1 10.9 7 17 7 17Z"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <circle cx="7" cy="6.8" r="1.8" fill="currentColor" />
          </svg>
        </span>
        <span className="nav-loc">SEATTLE,&nbsp;WA</span>
      </div>

      <div className={`nav-pill ${scrolled ? "compact" : ""}`}>
        <div className="nav-id">
          <button className="nav-avatar" onClick={() => go("home")} aria-label="Home">
            <img src={profileImg} alt="Sukrit Phaopanich" />
          </button>
        </div>
        <div className="avail-tag">
          <a
            className="avail-text"
            href={linkedin}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={scrolled ? 0 : -1}
            aria-hidden={!scrolled}
          >
            Contact me
          </a>
        </div>
        <span className="avail-dot" aria-hidden="true" />
        <div className="nav-links">
          <button
            className={`nav-link ${!onAbout ? "is-on" : ""}`}
            aria-current={!onAbout ? "page" : undefined}
            onClick={() => go("work")}
          >
            Work
          </button>
          <button
            className={`nav-link ${onAbout ? "is-on" : ""}`}
            aria-current={onAbout ? "page" : undefined}
            onClick={() => go("about")}
          >
            About
          </button>
          <button className="nav-link" onClick={() => go("contact")}>
            Contact
          </button>
          <button className="nav-cta" type="button" onClick={onOpenContact}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
              <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            Work with me
          </button>
        </div>
        <button
          className="nav-burger"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
          <span />
        </button>
      </div>

      <div className="nav-side right">
        <button
          className={`nav-wave ${musicOn ? "playing" : ""}`}
          type="button"
          aria-label="Play music"
          aria-pressed={musicOn}
          onClick={onToggleMusic}
        >
          <svg className="nav-wave-svg" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <g className="nav-wave-travel">
              <path
                className="nav-wave-path"
                d="M-20 20 C -17.5 11.1 -12.5 11.1 -10 20 C -7.5 28.9 -2.5 28.9 0 20 C 2.5 11.1 7.5 11.1 10 20 C 12.5 28.9 17.5 28.9 20 20 C 22.5 11.1 27.5 11.1 30 20 C 32.5 28.9 37.5 28.9 40 20 C 42.5 11.1 47.5 11.1 50 20 C 52.5 28.9 57.5 28.9 60 20"
              />
            </g>
          </svg>
        </button>
        <button
          className="day-toggle"
          onClick={onToggleTheme}
          aria-label="Toggle day / night theme"
          aria-pressed={theme === "night"}
          type="button"
        >
          <svg className="toggle-ico ico-sun" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4" fill="currentColor" />
            <g fill="currentColor">
              <circle cx="12" cy="3.6" r="1.15" />
              <circle cx="12" cy="20.4" r="1.15" />
              <circle cx="3.6" cy="12" r="1.15" />
              <circle cx="20.4" cy="12" r="1.15" />
              <circle cx="6.1" cy="6.1" r="1.15" />
              <circle cx="17.9" cy="6.1" r="1.15" />
              <circle cx="6.1" cy="17.9" r="1.15" />
              <circle cx="17.9" cy="17.9" r="1.15" />
            </g>
          </svg>
          <svg className="toggle-ico ico-moon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 14.2A8 8 0 1 1 10.3 4.2a6.4 6.4 0 1 0 9.7 10z" fill="currentColor" />
          </svg>
        </button>
      </div>

      <div className={`menu-overlay ${open ? "is-open" : ""}`} role="dialog" aria-modal="true">
        <ul className="menu-links">
          <li>
            <button className={`menu-link ${!onAbout ? "is-on" : ""}`} onClick={() => go("work")}>
              <span className="num">01</span>
              <span className="word">Work</span>
            </button>
          </li>
          <li>
            <button className={`menu-link ${onAbout ? "is-on" : ""}`} onClick={() => go("about")}>
              <span className="num">02</span>
              <span className="word">About</span>
            </button>
          </li>
          <li>
            <button className="menu-link" onClick={() => go("contact")}>
              <span className="num">03</span>
              <span className="word">Contact</span>
            </button>
          </li>
          <li>
            <button
              className="menu-link"
              onClick={() => {
                setOpen(false);
                onOpenContact();
              }}
            >
              <span className="num">04</span>
              <span className="word">Work with me</span>
            </button>
          </li>
        </ul>
        <p className="menu-footer">SEATTLE, WA</p>
      </div>
    </nav>
  );
};
